// Color constants
export const COMPLIANCE_COLORS = {
  SUCCESS: 'rgba(168, 244, 168, 0.2)',
  ERROR: 'rgba(255, 77, 78, 0.2)',
  WARNING: 'rgba(250, 174, 19, 0.2)',
  TRANSPARENT: 'transparent',
};

/**
 * Returns background color based on observation status (for StrengthTestTable)
 * @param {string} observation - Observation value ('OK', 'NO OK', 'NC', 'VNE')
 * @returns {string} - RGBA color string
 */
export const getBackgroundColorByObservation = (observation) => {
  if (observation === 'OK') {
    return COMPLIANCE_COLORS.SUCCESS;
  }
  if (observation === 'NO OK' || observation === 'NC') {
    return COMPLIANCE_COLORS.ERROR;
  }
  if (observation === 'VNE') {
    return COMPLIANCE_COLORS.WARNING;
  }
  return COMPLIANCE_COLORS.TRANSPARENT;
};

/**
 * Returns background color based on compliance status (for CustomStatisticalControlBatchDetail)
 * @param {boolean|number|null} compliance - Compliance value (true, false, 1, 0, null)
 * @param {number|null} compliance90 - Optional secondary compliance for dual display (caso 1/0)
 * @returns {string} - RGBA color string
 */
export const getBackgroundColorByCompliance = (
  compliance,
  compliance90 = null,
) => {
  // Si hay compliance90, lógica dual
  if (compliance90 !== null) {
    if (compliance === true || compliance === 1 || compliance90 === 1) {
      return COMPLIANCE_COLORS.SUCCESS;
    }
    if ((compliance === false || compliance === 0) && compliance90 === 0) {
      return COMPLIANCE_COLORS.ERROR;
    }
    if (compliance === null || compliance === undefined) {
      if (compliance90 === 1) return COMPLIANCE_COLORS.SUCCESS;
      if (compliance90 === 0) return COMPLIANCE_COLORS.WARNING;
    }
    return COMPLIANCE_COLORS.TRANSPARENT;
  }

  // Lógica para compliance único
  if (compliance === true || compliance === 1) {
    return COMPLIANCE_COLORS.SUCCESS;
  }
  if (compliance === false || compliance === 0) {
    return COMPLIANCE_COLORS.ERROR;
  }
  if (compliance === null || compliance === undefined) {
    return COMPLIANCE_COLORS.WARNING;
  }
  return COMPLIANCE_COLORS.TRANSPARENT;
};

// Helper functions outside component
const getNumber = (key) => {
  const splitString = key.split('');
  let reverseArray = splitString.reverse();
  const joinArray = reverseArray.join('');
  const dayInverse = joinArray.substring(
    joinArray.indexOf('_') + 1,
    joinArray.length,
  );
  const dayInverseInt = dayInverse.substring(0, dayInverse.indexOf('_'));
  const splitdayInverse = dayInverseInt.split('');
  reverseArray = splitdayInverse.reverse();
  return parseInt(reverseArray.join(''));
};

const getAveStrength = (strengthExtras, day) => {
  const aveStrengthList = [];
  Object.keys(strengthExtras).forEach((val) => {
    const name = `ave_strength_${day.toString()}`;
    if (val.includes(name)) {
      aveStrengthList.push({
        [val]: strengthExtras[val],
      });
    }
  });
  return aveStrengthList;
};

const getCompressiveStrength = (strengthExtras, day) => {
  const compressiveStrengthList = [];
  Object.keys(strengthExtras).forEach((val) => {
    const name = `compressive_strength_${day.toString()}`;
    if (val.includes(name)) {
      compressiveStrengthList.push({
        [val]: strengthExtras[val],
      });
    }
  });
  return compressiveStrengthList;
};

const getObservationStrength = (strengthExtras, day) => {
  const observationStrengthList = [];
  Object.keys(strengthExtras).forEach((val) => {
    const name = `observation_${day.toString()}`;
    if (val.includes(name)) {
      observationStrengthList.push({
        [val]: strengthExtras[val],
      });
    }
  });
  return observationStrengthList;
};

const getExtraNumbers = (delivery) => {
  const extraNumbers = [];
  if (delivery.laboratory_note_data?.strength_extra) {
    const keys = Object.keys(delivery.laboratory_note_data.strength_extra);
    keys.forEach((key) => {
      if (key.includes('compressive_strength')) {
        const day = getNumber(key);
        extraNumbers.push({
          batch_code: delivery.batch_code,
          mash_control: delivery.mash_control,
          mash_control_extra: delivery.mash_control_extra,
          docket_number: delivery.docket_number,
          reference_number: delivery.laboratory_note_data.reference_number,
          extra_day: day,
          ave_strength: getAveStrength(
            delivery.laboratory_note_data.strength_extra,
            day,
          ),
          compressive_strength: getCompressiveStrength(
            delivery.laboratory_note_data.strength_extra,
            day,
          ),
          observation: getObservationStrength(
            delivery.laboratory_note_data.strength_extra,
            day,
          ),
        });
      }
    });
  }
  return extraNumbers;
};

const getStrengthExtras = (deliveryList) => {
  const strengthExtras = [];
  deliveryList.forEach((delivery) => {
    strengthExtras.push({
      docket_number: delivery.docket_number,
      reference_number: delivery.laboratory_note_data
        ? delivery.laboratory_note_data.reference_number
        : '',
      strength_extra_number: getExtraNumbers(delivery),
    });
  });
  return strengthExtras;
};

const getTables = (deliveryList) => {
  const strengthExtras = getStrengthExtras(deliveryList);
  if (strengthExtras.length !== 0) {
    const strenghtEXTRS = [];
    const strenghtEXTRSTables = [];
    strengthExtras.forEach((str) => {
      str.strength_extra_number.forEach((strNum) => {
        if (strenghtEXTRS.length === 0) {
          strenghtEXTRS.push(strNum);
        } else if (
          strenghtEXTRS.some(
            (x) =>
              x.docket_number === strNum.docket_number &&
              x.extra_day === strNum.extra_day,
          )
        ) {
        } else {
          strenghtEXTRS.push(strNum);
        }
      });
    });
    strenghtEXTRS.forEach((x) => {
      const sameDay = deliveryList.filter((d) => {
        if (d.laboratory_note_data) {
          const keys = Object.keys(d.laboratory_note_data.strength_extra);
          let hasDay = false;
          keys.forEach((key) => {
            if (key.includes('compressive_strength')) {
              const day = getNumber(key);
              if (day === x.extra_day) {
                hasDay = true;
              }
            }
          });
          return hasDay;
        }
        return false;
      });
      strenghtEXTRSTables.push({ extraDay: x.extra_day, sameDay });
    });
    strenghtEXTRSTables.sort((a, b) => a.extraDay - b.extraDay);

    const key = 'extraDay';
    const arrayUniqueByKey = [
      ...new Map(strenghtEXTRSTables.map((item) => [item[key], item])).values(),
    ];
    return arrayUniqueByKey.reduce(
      (r, o) => {
        r[
          o.extraDay < 28
            ? o.extraDay < 3
              ? 'before3Tables'
              : o.extraDay < 7
              ? 'before7Tables'
              : 'before28Tables'
            : 'afterTables'
        ].push(o);
        return r;
      },
      {
        before3Tables: [],
        before7Tables: [],
        before28Tables: [],
        afterTables: [],
        d3Table: deliveryList.filter(
          (d) => d.laboratory_note_data?.ave_strength_3 != null,
        ),
        d7Table: deliveryList.filter(
          (d) => d.laboratory_note_data?.ave_strength_7 != null,
        ),
        d60Table: deliveryList.filter(
          (d) => d.laboratory_note_data?.ave_strength_60 != null,
        ),
      },
    );
  }
};

const getPosition = (total) => {
  if (total < 21) {
    return 1;
  } else {
    return Math.floor(0.05 * total + 1);
  }
};

const hasNonConformity = (concreteBatches = []) => {
  return (
    concreteBatches?.length > 0 &&
    concreteBatches.some((cb) => cb.non_conformity)
  );
};

const shouldShowCreateNC = (concreteBatches = []) => {
  return (
    concreteBatches.length > 0 &&
    concreteBatches.every((cb) => cb.non_conformity === null) &&
    (concreteBatches[0].compliance === false ||
      concreteBatches[0].compliance90 === 0)
  );
};

const getNonConformityBatch = (concreteBatches = []) => {
  const batch = concreteBatches.find((cb) => cb.non_conformity)?.non_conformity;
  return batch;
};

export {
  getTables,
  getPosition,
  hasNonConformity,
  shouldShowCreateNC,
  getNonConformityBatch,
};
